import { useLayoutEffect, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import { useDialog } from 'src/core/helper/use-dialog';
import { useTranslation } from 'react-i18next';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import MuiButton from '@mui/material/Button';
import { useSetPageActionContext } from 'src/shared/layout/page-layout/page-layout.context-provider';
import { DataGrid } from '@app/feature/data-grid/data-grid';
import { Snackbar } from '@shared/snackbar/snackbar';
import { useUserColumn } from '@app/feature/user/generic-user/user-data-grid/use-user-column';
import { useUserFilter } from '@app/feature/user/generic-user/user-data-grid/use-user-filter';
import { useGetAdminsPaginatedQuery } from '@service/admin.service';
import { SortOrder } from '@delmonicos/shared-sdk/libs/emsp/src/libs/core/src/utils/mongo/pagination/models/pagination.model';
import { useDataGridSearchParams } from '@app/feature/data-grid/use-data-grid-search-params/use-data-grid-search-params';
import {
  DEFAULT_DATA_GRID_MODEL,
  DEFAULT_PAGE_SIZE,
} from '@app/config/data-grid.config';
import { DataGridQueryModel } from '@model/data-grid.model';
import { GetAdminsModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/back-office/src/models/users/get-users.model';
import { BackOfficeUserModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/back-office/src/models/users/back-office-user.model';
import { AdminUpdate } from '@app/feature/user/administrator/administrator-update/administrator-update';
import { CreateAdmin } from '@app/feature/user/administrator/administrator-create/administrator-create';

export function AdministratorList(): React.JSX.Element {
  const { t } = useTranslation();

  const [isErrorUpdating, setIsErrorUpdating] = useState(false);
  const [isErrorCreating, setIsErrorCreating] = useState(false);

  const {
    isOpen: isCreateDialogOpen,
    onOpen: onOpenCreateDialog,
    onClose: onCloseCreateDialog,
  } = useDialog();

  const handleCloseCreateDialog = () => {
    onCloseCreateDialog();
    setIsErrorCreating(false);
  };

  const [userUpdate, setUserUpdate] = useState<BackOfficeUserModel | null>(
    null,
  );

  const columns = useUserColumn({ onUpdateUser: () => {} });

  // PAGINATION & FILTERS //////////////////////////////////////////////////////
  const filterDefinitionModel = useUserFilter();

  const { model: urlModel } = useDataGridSearchParams(DEFAULT_DATA_GRID_MODEL);
  const [model, setModel] = useState<DataGridQueryModel>(urlModel);

  const [params, setParams] = useState<GetAdminsModel>({
    sortField: 'updatedAt',
    sortOrder: SortOrder.DESC,
    limit: DEFAULT_DATA_GRID_MODEL.paginate?.pageSize,
    offset:
      (DEFAULT_DATA_GRID_MODEL.paginate?.page ?? 0) *
      (DEFAULT_DATA_GRID_MODEL.paginate?.pageSize ?? 0),
    textSearch: urlModel.textSearch || undefined,
  });

  const onModelChange = (newModel: DataGridQueryModel) => {
    setModel(newModel);

    setParams((prev) => {
      const pageSize =
        newModel.paginate?.pageSize ?? prev.limit ?? DEFAULT_PAGE_SIZE;
      const page = newModel.paginate?.page ?? 0;

      return {
        ...prev,
        limit: pageSize,
        offset: page * pageSize,
        textSearch: newModel.textSearch || undefined,
      };
    });
  };

  // DATA FETCHING /////////////////////////////////////////////////////////////
  const {
    data: paginatedData,
    isFetching,
    isError,
  } = useGetAdminsPaginatedQuery(params);

  const setPageAction = useSetPageActionContext();

  const handleCloseUserUpdateDialog = () => {
    setUserUpdate(null);
    setIsErrorUpdating(false);
  };

  useLayoutEffect(() => {
    const createAdminButton = (
      <MuiButton
        key="create-admin"
        onClick={onOpenCreateDialog}
        variant="contained">
        <AddOutlinedIcon />
        {t('form.create-button')}
      </MuiButton>
    );

    setPageAction([createAdminButton]);
  }, [t, onOpenCreateDialog, setPageAction]);

  return (
    <>
      <DataGrid
        columns={columns}
        filterDefinitionModel={filterDefinitionModel}
        loading={isFetching}
        rows={paginatedData?.docs ?? []}
        rowCount={paginatedData?.totalDocs ?? 0}
        model={model}
        onModelChange={onModelChange}
      />

      {isError && (
        <Snackbar variant="error" message={t('error.get-list-user-query')} />
      )}

      {isErrorUpdating && (
        <Snackbar variant="error" message={t('error.patch-user-mutation')} />
      )}

      {isErrorCreating && (
        <Snackbar variant="error" message={t('error.create-user-mutation')} />
      )}

      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
        <CreateAdmin
          onCancel={handleCloseCreateDialog}
          onSuccess={handleCloseCreateDialog}
          onError={() => setIsErrorCreating(true)}
        />
      </Dialog>

      <Dialog open={!!userUpdate} onClose={handleCloseUserUpdateDialog}>
        {userUpdate && (
          <AdminUpdate
            admin={userUpdate}
            onCancel={handleCloseUserUpdateDialog}
            onSuccess={handleCloseUserUpdateDialog}
            onError={() => setIsErrorUpdating(true)}
          />
        )}
      </Dialog>
    </>
  );
}
